import React from 'react'
import { formatMoney } from '../../../utils/amount'
import { formatPct } from './helpers'

const ACCENT_CLASSES = {
  default: '',
  success: 'bg-success-light/40 border-success-main/30',
  warning: 'bg-warning-light/40 border-warning-main/30',
  error: 'bg-error-light/40 border-error-main/30',
  info: 'bg-info-light/40 border-info-main/30',
}

const VALUE_CLASSES = {
  default: 'text-text-primary',
  success: 'text-success-main',
  warning: 'text-warning-main',
  error: 'text-error-main',
  info: 'text-info-main',
}

const KpiCard = ({ label, value, sublabel, accent = 'default' }) => (
  <div
    className={`flex-1 min-w-[190px] rounded-2xl border border-divider bg-paper p-5 ${ACCENT_CLASSES[accent] || ''}`}
    data-testid={`kpi-card-${label}`}
  >
    <p className="text-sm text-text-secondary font-medium">{label}</p>
    <p className={`text-2xl font-semibold mt-2 ${VALUE_CLASSES[accent] || VALUE_CLASSES.default}`}>{value}</p>
    <p className="text-xs text-text-secondary mt-1">{sublabel}</p>
  </div>
)

const safeCount = (value) => (typeof value === 'number' ? value : '—')
const safeMoney = (value) => (typeof value === 'number' ? formatMoney(value) : '—')

/**
 * The `cleared` / `pending` / `returnedDeclined` KPI groups share the same
 * shape: { count, pct, amount }. This renders that group consistently.
 */
const bucketSublabel = (bucket) => `${formatPct(bucket?.pct)} · ${safeMoney(bucket?.amount)}`

const KpiCards = ({ kpis = {} }) => {
  return (
    <div className="flex flex-wrap gap-4">
      <KpiCard label="Total Submitted" value={safeCount(kpis.totalSubmitted)} sublabel="Payments submitted" />
      <KpiCard
        label="Cleared"
        value={safeCount(kpis.cleared?.count)}
        sublabel={bucketSublabel(kpis.cleared)}
        accent="success"
      />
      <KpiCard
        label="Pending"
        value={safeCount(kpis.pending?.count)}
        sublabel={bucketSublabel(kpis.pending)}
        accent="warning"
      />
      <KpiCard
        label="Returned/Declined"
        value={safeCount(kpis.returnedDeclined?.count)}
        sublabel={bucketSublabel(kpis.returnedDeclined)}
        accent="error"
      />
      {/*
        Unlike cleared/pending/returnedDeclined, `needsReview` is a plain count (not a
        { count, pct, amount } bucket) — see the backend contract in vlg-server MR !1055 —
        so there's no pct/amount to show in the sublabel. accent="info" matches
        getStatusColorKey('needsReview') so the tile reads consistently with the status
        chip/color used elsewhere for these rows.
      */}
      <KpiCard
        label="Needs Review"
        value={safeCount(kpis.needsReview)}
        sublabel="Parked pending review"
        accent="info"
      />
      {/*
        "Processing Fees" and "Net Deposit (Est.)" cards are hidden pending a real
        processor-fee data source — we don't currently ingest the per-transaction
        cut each processor takes, so `kpis.processingFees` / `kpis.netDeposit` are
        placeholder values, not real data. The backend contract and slice still
        pass these fields through; only the UI is hidden. Restore the two
        <KpiCard> entries below once fee ingestion lands:
        <KpiCard label="Processing Fees" value={safeMoney(kpis.processingFees)} sublabel="Total fees deducted" />
        <KpiCard label="Net Deposit (Est.)" value={safeMoney(kpis.netDeposit)} sublabel="Estimated net to bank" accent="info" />
      */}
    </div>
  )
}

export default KpiCards
